import { useCallback, useEffect, useRef, useState } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  useIsMutating,
  useQueryClient,
} from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import {
  primaryNavigation,
  type PrimarySurface,
  type PlayerVerticalSliceView,
} from '@aztec-realms/shared-types';
import { GameShell } from '@aztec-realms/ui';

import { useVerticalSliceQuery, verticalSliceQueryKey } from './data/use-vertical-slice';
import { releaseDisplayedDecodeSelection } from './data/api-vertical-slice';
import { useOfflineRealmSnapshot } from './data/offline-realm';
import { JourneySafetyProvider, useJourneySafety } from './journey-safety';
import { useNetworkStatus } from './network-status';
import {
  CodexScreen,
  DecodeScreen,
  ExploreScreen,
  HomeScreen,
  OnboardingScreen,
  SoloScreen,
  TribeScreen,
} from './player-screens';
import { PresentationSettingsProvider } from './presentation-settings';
import { PwaStatus } from './PwaStatus';
import { PlayerLoginScreen } from './PlayerLoginScreen';
import {
  clearPendingAccountEmail,
  isPublicAccountPath,
  pendingAccountEmail,
  PublicAccountScreen,
} from './PublicAccountScreens';
import { NotFoundScreen, SettingsScreen, VisualKitScreen } from './screens';
import { OfflineRealmScreen } from './OfflineRealmScreen';
import { ShopScreen } from './ShopScreen';
import {
  BROWSER_SESSION_ACTIVITY_EVENT,
  BROWSER_SESSION_INVALIDATED_EVENT,
  isAuthenticationRequired,
  type BrowserSessionInvalidatedDetail,
} from './data/browser-api';
import { browserSessionIdleMilliseconds } from './data/session-policy';
import { terminateBrowserSession } from './data/session-teardown';
import { ONBOARDING_COMPLETE_KEY, shouldOfferFirstTimeGuide } from './OnboardingGuide';
import { mobileEntryRoute } from './player-routing';
import { realmNextStep } from './realm-next-step';
import { EarnedInbox } from './EarnedInbox';
import { reconcilePushOwner } from './data/push-installation';
import { SceneStartupBoundary } from './SceneStartup';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function surfaceForPath(pathname: string): PrimarySurface {
  if (pathname.startsWith('/explore')) return 'explore';
  if (pathname.startsWith('/decode')) return 'decode';
  if (pathname.startsWith('/codex')) return 'codex';
  if (pathname.startsWith('/solo')) return 'codex';
  if (pathname.startsWith('/tribe')) return 'tribe';
  return 'home';
}

function pathShowsMission(pathname: string): boolean {
  return ['/', '/home', '/explore', '/decode', '/codex', '/tribe'].includes(pathname);
}

export function AppRoutes() {
  const location = useLocation();
  const cache = useQueryClient();
  const previousPath = useRef(location.pathname);
  useEffect(() => {
    const leavingDecode = previousPath.current === '/decode' && location.pathname !== '/decode';
    previousPath.current = location.pathname;
    const current = cache.getQueryData<PlayerVerticalSliceView>(verticalSliceQueryKey)?.decode;
    if (leavingDecode && releaseDisplayedDecodeSelection(current)) {
      void cache.invalidateQueries({ queryKey: verticalSliceQueryKey });
    }
  }, [cache, location.pathname]);
  const navigate = useNavigate();
  const online = useNetworkStatus();
  const [sessionInvalidated, setSessionInvalidated] = useState(false);
  const [anonymousLoginRequired, setAnonymousLoginRequired] = useState(false);
  const bootstrap = useVerticalSliceQuery({ enabled: !sessionInvalidated });
  const sessionCleanupStarted = useRef(false);
  const sessionExpiryTimer = useRef<number | undefined>(undefined);
  // A reconnect can temporarily reset the failed anonymous query to pending. Keep the login
  // mounted (and its in-memory draft intact) until a successful authenticated bootstrap arrives.
  const anonymousProbeRejected = !bootstrap.data && isAuthenticationRequired(bootstrap.error);
  if (anonymousProbeRejected && !anonymousLoginRequired) setAnonymousLoginRequired(true);
  if (bootstrap.data && anonymousLoginRequired) setAnonymousLoginRequired(false);
  const authenticationRequired =
    sessionInvalidated || anonymousLoginRequired || anonymousProbeRejected;
  const offlineCandidate =
    !authenticationRequired && !bootstrap.data && (!online || bootstrap.isError);
  const offlineRealm = useOfflineRealmSnapshot(offlineCandidate);
  const { errorUpdatedAt: bootstrapErrorUpdatedAt, refetch: refetchBootstrap } = bootstrap;
  const journey = useJourneySafety();
  const pendingMutations = useIsMutating();
  const currentSurface = surfaceForPath(location.pathname);
  const showFixtureDebug =
    import.meta.env.DEV && new URLSearchParams(location.search).get('debug') === '1';
  const mission = bootstrap.data?.settlement.activeMission;
  const nextMissionStep = mission?.steps.find((step) => !step.complete)?.id;
  const offlineFallback = offlineCandidate;

  const clearInvalidSession = useCallback(
    (detail?: BrowserSessionInvalidatedDetail) => {
      const isAnonymousProbe =
        !bootstrap.data && (!detail || detail.code === 'authentication_required');
      if (isAnonymousProbe) return;

      setSessionInvalidated(true);
      if (detail?.code === 'signed_out') return;
      if (sessionCleanupStarted.current) return;
      sessionCleanupStarted.current = true;
      void terminateBrowserSession(queryClient, { online: false, preservePush: true }).catch(
        () => undefined,
      );
    },
    [bootstrap.data],
  );

  const ownerId = bootstrap.data?.source === 'api' ? bootstrap.data.profile.userId : undefined;
  useEffect(() => {
    if (ownerId && !authenticationRequired) void reconcilePushOwner(ownerId).catch(() => undefined);
  }, [ownerId, authenticationRequired, online]);

  const armSessionExpiry = useCallback(() => {
    if (sessionExpiryTimer.current !== undefined) {
      window.clearTimeout(sessionExpiryTimer.current);
      sessionExpiryTimer.current = undefined;
    }
    if (!bootstrap.data || sessionInvalidated) return;
    sessionExpiryTimer.current = window.setTimeout(
      () =>
        clearInvalidSession({
          code: 'session_expired',
          message: 'Session has expired',
        }),
      browserSessionIdleMilliseconds,
    );
  }, [bootstrap.data, clearInvalidSession, sessionInvalidated]);

  useEffect(() => {
    const handleSessionInvalidated = (event: Event) => {
      clearInvalidSession((event as CustomEvent<BrowserSessionInvalidatedDetail>).detail);
    };
    window.addEventListener(BROWSER_SESSION_INVALIDATED_EVENT, handleSessionInvalidated);
    return () =>
      window.removeEventListener(BROWSER_SESSION_INVALIDATED_EVENT, handleSessionInvalidated);
  }, [clearInvalidSession]);

  useEffect(() => {
    const handleSessionActivity = () => armSessionExpiry();
    window.addEventListener(BROWSER_SESSION_ACTIVITY_EVENT, handleSessionActivity);
    armSessionExpiry();
    return () => {
      window.removeEventListener(BROWSER_SESSION_ACTIVITY_EVENT, handleSessionActivity);
      if (sessionExpiryTimer.current !== undefined) {
        window.clearTimeout(sessionExpiryTimer.current);
        sessionExpiryTimer.current = undefined;
      }
    };
  }, [armSessionExpiry]);

  useEffect(() => {
    if (!offlineFallback || !online || !offlineRealm.snapshot) return;
    const reconnectProbe = window.setTimeout(() => void refetchBootstrap(), 2_500);
    return () => window.clearTimeout(reconnectProbe);
  }, [bootstrapErrorUpdatedAt, offlineFallback, offlineRealm.snapshot, online, refetchBootstrap]);

  const entryRoute = (() => {
    if (!bootstrap.data || !['/', '/home'].includes(location.pathname)) return undefined;
    const deterministic = Boolean(
      (window as Window & { __AZR_E2E__?: { deterministic?: boolean } }).__AZR_E2E__?.deterministic,
    );
    const dismissed = window.localStorage.getItem(ONBOARDING_COMPLETE_KEY) === 'true';
    const offerGuide =
      !journey.active &&
      shouldOfferFirstTimeGuide({
        completed: bootstrap.data.profile.onboardingCompleted,
        dismissed,
        deterministic,
      });
    if (offerGuide) {
      return '/onboarding';
    }
    const mobileRoute = mobileEntryRoute({
      pathname: location.pathname,
      search: location.search,
      mobile: window.matchMedia?.('(max-width: 699px)').matches ?? false,
      deterministic,
    });
    return mobileRoute;
  })();

  const content = (() => {
    if (isPublicAccountPath(location.pathname)) {
      return <PublicAccountScreen />;
    }

    if (authenticationRequired) {
      return (
        <PlayerLoginScreen
          initialEmail={pendingAccountEmail()}
          notice={
            typeof location.state?.privacyRequest === 'string'
              ? `Your account is closed. Removal request ${location.state.privacyRequest} is recorded for review; retained records have not been erased.`
              : sessionInvalidated
                ? 'Your session ended. Sign in again to continue safely.'
                : new URLSearchParams(location.search).get('verified') === '1'
                  ? 'Email verified. Sign in to begin your path.'
                  : new URLSearchParams(location.search).get('password-reset') === '1'
                    ? 'Password changed. Sign in with your new password.'
                    : undefined
          }
          onAuthenticated={async () => {
            clearPendingAccountEmail();
            sessionCleanupStarted.current = false;
            setSessionInvalidated(false);
            await bootstrap.refetch();
          }}
        />
      );
    }

    // Decide the mobile landing route before mounting Home or its renderer.
    if (entryRoute) return <Navigate replace to={entryRoute} />;

    if (
      location.pathname === '/onboarding' ||
      location.pathname === '/welcome' ||
      location.pathname === '/guide'
    ) {
      return (
        <Routes>
          <Route element={<OnboardingScreen />} path="/onboarding" />
          <Route element={<OnboardingScreen />} path="/guide" />
          <Route element={<Navigate replace to="/onboarding" />} path="/welcome" />
          <Route element={<Navigate replace to="/onboarding" />} path="*" />
        </Routes>
      );
    }

    return (
      <GameShell
        inboxAction={
          online &&
          !offlineFallback &&
          !bootstrap.isError &&
          bootstrap.data?.source === 'api' &&
          bootstrap.data.profile.userId ? (
            <EarnedInbox
              ownerId={bootstrap.data.profile.userId}
              membershipScope={JSON.stringify([
                bootstrap.data.tribe.current?.id,
                bootstrap.data.tribe.current?.currentUserRole,
                bootstrap.data.tribe.current?.members.map((member) => member.memberId).sort(),
              ])}
              passive={journey.safety.mode === 'journey-passive'}
            />
          ) : undefined
        }
        compactQuest={currentSurface === 'home'}
        currentSurface={currentSurface}
        journeySafety={journey.safety}
        loading={!bootstrap.data && !offlineRealm.snapshot && bootstrap.isPending}
        navItems={primaryNavigation}
        onCancelJourney={journey.cancelJourney}
        onNavigate={navigate}
        onStopJourney={journey.stopJourney}
        offline={!online || offlineFallback}
        playerLevel={bootstrap.data?.profile.level ?? offlineRealm.snapshot?.profile.level ?? 1}
        playerAvatarLabel={bootstrap.data?.profile.avatarLabel}
        playerAvatarUrl={bootstrap.data?.profile.avatarAssetUrl}
        playerName={
          bootstrap.data?.profile.displayName ??
          offlineRealm.snapshot?.profile.displayName ??
          'Wayfinder'
        }
        quest={
          mission && pathShowsMission(location.pathname)
            ? mission.progress >= mission.target && bootstrap.data
              ? realmNextStep(bootstrap.data)
              : {
                  title: mission.title,
                  progressLabel: `${mission.progress} of ${mission.target} complete`,
                  href:
                    nextMissionStep === 'discover'
                      ? '/explore'
                      : nextMissionStep === 'decode'
                        ? '/decode'
                        : '/home',
                }
            : undefined
        }
        resources={offlineFallback ? [] : (bootstrap.data?.settlement.resources ?? [])}
      >
        {showFixtureDebug && bootstrap.data?.source === 'demo_fixture' && (
          <span className="demo-fixture-badge" role="status">
            Demo realm · local fixture progress
          </span>
        )}
        {offlineFallback ? (
          <OfflineRealmScreen
            onCleared={() => void offlineRealm.refresh()}
            snapshot={offlineRealm.snapshot}
          />
        ) : (
          <Routes>
            <Route element={<HomeScreen />} path="/" />
            <Route element={<HomeScreen />} path="/home" />
            <Route element={<ExploreScreen />} path="/explore" />
            <Route element={<DecodeScreen />} path="/decode" />
            <Route element={<CodexScreen />} path="/codex" />
            <Route element={<SoloScreen />} path="/solo" />
            <Route element={<TribeScreen />} path="/tribe" />
            <Route element={<ShopScreen />} path="/shop" />
            <Route
              element={
                <SettingsScreen
                  ownerId={ownerId}
                  interactionLocked={journey.safety.mode === 'journey-passive'}
                />
              }
              path="/profile"
            />
            <Route
              element={
                <SettingsScreen
                  ownerId={ownerId}
                  interactionLocked={journey.safety.mode === 'journey-passive'}
                />
              }
              path="/settings"
            />
            <Route
              element={import.meta.env.DEV ? <VisualKitScreen /> : <Navigate replace to="/" />}
              path="/dev/visual-kit"
            />
            <Route element={<NotFoundScreen />} path="*" />
          </Routes>
        )}
      </GameShell>
    );
  })();

  return (
    <SceneStartupBoundary
      routeKey={location.key}
      waitingForData={!bootstrap.data && bootstrap.isPending}
      enabled={
        online &&
        !offlineFallback &&
        !authenticationRequired &&
        !isPublicAccountPath(location.pathname) &&
        !['/onboarding', '/welcome', '/guide'].includes(location.pathname) &&
        journey.safety.mode !== 'journey-passive'
      }
      phase={
        entryRoute === '/explore' || location.pathname === '/explore'
          ? 'location'
          : ['/', '/home'].includes(location.pathname) && !entryRoute
            ? 'home'
            : location.pathname === '/decode'
              ? 'decode'
              : undefined
      }
      onLeave={() => navigate('/settings')}
      onAlternative={currentSurface === 'explore' ? () => navigate('/decode') : undefined}
    >
      {content}
      <PwaStatus
        navigationKey={location.pathname}
        presentation={
          authenticationRequired || isPublicAccountPath(location.pathname) ? 'account' : 'game'
        }
        safeToUpdate={
          !journey.active &&
          pendingMutations === 0 &&
          !isPublicAccountPath(location.pathname) &&
          !['/onboarding', '/welcome', '/guide', '/settings', '/profile', '/shop'].includes(
            location.pathname,
          ) &&
          (authenticationRequired || currentSurface !== 'decode')
        }
      />
    </SceneStartupBoundary>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PresentationSettingsProvider>
        <JourneySafetyProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </JourneySafetyProvider>
      </PresentationSettingsProvider>
    </QueryClientProvider>
  );
}
